import { ReactNode } from 'react';
import { useTranslation } from '@/i18n';
import { useAuth } from './useAuth';
import { useTheme } from '@/theme';
import { useAiEnabled } from '@/hooks';
import { LockIcon } from '@/features/ai-assistant/components/ChatIcons';

interface AiFeatureGuardProps {
  children: ReactNode;
}

/**
 * Protects routes that are only meaningful when the tenant has AI enabled.
 *
 * The sidebar already hides the AI menu, but hiding a menu item does not stop a
 * bookmarked or hand-typed URL. Without this, a user holding the underlying CRUD
 * permission could still open a fully functional AI admin page for a tenant whose
 * AI is switched off — and every action on it would be rejected by the backend.
 *
 * Mirrors the locked state AiAssistantPage renders for itself.
 */
export const AiFeatureGuard = ({ children }: AiFeatureGuardProps) => {
  const { featuresLoaded } = useAuth();
  const aiEnabled = useAiEnabled();
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Render nothing until application-configuration lands, so a page never flashes
  // into view before the feature value is known.
  if (!featuresLoaded) {
    return null;
  }

  if (aiEnabled) {
    return <>{children}</>;
  }

  return (
    <div>
      <div
        style={{
          backgroundColor: theme.colors.backgroundCard,
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.card,
          padding: theme.spacing.xl,
          textAlign: 'center',
          maxWidth: '500px',
          margin: '80px auto',
        }}
      >
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: theme.borderRadius.full,
            backgroundColor: theme.colors.backgroundMuted,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: `0 auto ${theme.spacing.lg}`,
          }}
          aria-hidden="true"
        >
          <LockIcon size={28} color={theme.colors.textMuted} />
        </div>
        <h2
          style={{
            fontSize: theme.typography.fontSizeXl,
            fontWeight: theme.typography.fontWeightSemibold,
            color: theme.colors.textPrimary,
            marginBottom: theme.spacing.md,
          }}
        >
          {t('AiAssistant:FeatureNotEnabled')}
        </h2>
        <p
          style={{
            fontSize: theme.typography.fontSizeBase,
            color: theme.colors.textSecondary,
          }}
        >
          {t('AiAssistant:ContactAdmin')}
        </p>
      </div>
    </div>
  );
};
